import React, { Dispatch, FunctionComponent, useState } from "react";
import CustomError from "./CustomError";

/* Validation */
import { Formik, useFormikContext } from "formik";
import { validationSchema } from "./formConfig";

/* API */
import axios from "axios";
import { Endpoints } from "@internal/common";
import { ClaimReportBuildingDamageFormData } from "./ClaimModels";
import FormAlert from "./components/FormAlert";
import { Button, Flex, Text, Title } from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";
import FormInputGroup from "./components/FormInputGroup";
import FormTextArea from "./components/FormTextArea";
import FormDropdown from "./components/FormDropdown";

export interface IProps {
  claimType: string;
}

interface FormData extends ClaimReportBuildingDamageFormData {
  claimType: string;
}

const ClaimReportBuilding: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState<FormData>({
    claimType: props.claimType,
    cvrNumber: "",
    policyNumber: "",
    contactPersonName: "",
    email: "",
    contactPersonPhoneNumber: "",
    propertyInspectorPhoneNumber: "",
    damagedPropertyAddress: "",
    isPropertyVATRegistered: "",
    propertyType: "",
    propertyUse: "",
    insuredByOther: "",
    insuranceCompanyDetails: "",
    damageDetails: "",
    insuranceItemsDamageDetails: "",
    damageDate: "",
    damageDiscovered: "",
    othersToBlame: "",
    whoToBlame: "",
    typeOfDamage: "",
    glassDamageDetails: "",
    theftDamageDetails: "",
    sanitationDamdageDetails: "",
    carpetDamageDetails: "",
    repairDamagePerson: [""],
    priceRepairEstimate: "",
    consent: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sendFormData = () => {
    setIsLoading(true);
    setApiError(false);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => {
        setIsLoading(false);
        setApiError(true);
      });
  };

  return (
    <>
      {submitted ? (
        <FormAlert
          severity="success"
          title="Tak for din anmeldelse"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse."
        />
      ) : (
        <>
          <Title size={5}>Anmeldelse af skade på bygning</Title>
          <Text>* Påkrævede felter</Text>
          <Formik
            enableReinitialize
            initialValues={{
              cvrNumber: formData.cvrNumber,
              policyNumber: formData.policyNumber,
              contactPersonName: formData.contactPersonName,
              email: formData.email,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              propertyInspectorPhoneNumber:
                formData.propertyInspectorPhoneNumber,
              damagedPropertyAddress: formData.damagedPropertyAddress,
              isPropertyVATRegistered: formData.isPropertyVATRegistered,
              propertyType: formData.propertyType,
              propertyUse: formData.propertyUse,
              insuredByOther: formData.insuredByOther,
              insuranceCompanyDetails: formData.insuranceCompanyDetails,
              damageDetails: formData.damageDetails,
              insuranceItemsDamageDetails: formData.insuranceItemsDamageDetails,
              damageDate: formData.damageDate,
              damageDiscovered: formData.damageDiscovered,
              othersToBlame: formData.othersToBlame,
              whoToBlame: formData.whoToBlame,
              typeOfDamage: formData.typeOfDamage,
              glassDamageDetails: formData.glassDamageDetails,
              theftDamageDetails: formData.theftDamageDetails,
              sanitationDamdageDetails: formData.sanitationDamdageDetails,
              carpetDamageDetails: formData.carpetDamageDetails,
              repairDamagePerson: formData.repairDamagePerson,
              priceRepairEstimate: formData.priceRepairEstimate,
              consent: formData.consent,
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            <RenderForm isLoading={isLoading} setFormData={setFormData} />
          </Formik>
        </>
      )}
      {apiError && (
        <FormAlert
          severity="error"
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
        />
      )}
    </>
  );
};

interface RenderProps {
  isLoading: boolean;
  setFormData: Dispatch<React.SetStateAction<FormData>>;
}

const RenderForm: FunctionComponent<RenderProps> = ({
  isLoading,
  setFormData,
}) => {
  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values: formData,
  } = useFormikContext<FormData>();

  const typeOfDamageDropdownChange = (value: string) => {
    setFormData({ ...formData, typeOfDamage: value });
  };

  const typeOfDamageDropdown = [
    "Brand",
    "Lynnedslag",
    "Kortslutning",
    "Eksplosion",
    "Stormskade",
    "Skybrud/tøbrud",
    "Vandskade",
    "Indbrud/tyveri",
    "Svamp",
    "Insektskade",
    "Påkørsel",
    "Glasskade",
    "Sanitetsskade",
    "Frostskade (rør)",
    "Rørskade (kloak)",
    "Rørskade (skjulte rør)",
    "Rørskade (andre stikledninger)",
    "Rørskade + siveskade",
    "Hærværk",
  ];

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="cvr-number"
          labelText="CVR-nummer *"
          name="cvrNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("cvrNumber", event.target.value);
            setFormData({
              ...formData,
              cvrNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.cvrNumber && errors.cvrNumber)}
          errorText={errors.cvrNumber}
          value={formData.cvrNumber}
        />
        <FormInput
          id="policy-number"
          labelText="Policenummer *"
          name="policyNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("policyNumber", event.target.value);
            setFormData({
              ...formData,
              policyNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.policyNumber && errors.policyNumber)}
          errorText={errors.policyNumber}
          value={formData.policyNumber}
        />
        <FormInput
          id="contactPersonName"
          labelText="Navn (kontaktperson) *"
          name="contactPersonName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonName", event.target.value);
            setFormData({
              ...formData,
              contactPersonName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.contactPersonName && errors.contactPersonName)}
          errorText={errors.contactPersonName}
          value={formData.contactPersonName}
        />
        <FormInput
          id="email"
          type={"email"}
          labelText="Email (kontaktperson) *"
          name="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
            setFormData({
              ...formData,
              email: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={formData.email}
        />
        <FormInput
          id="contactPersonPhoneNumber"
          labelText="Telefonnummer (kontaktperson) *"
          name="contactPersonPhoneNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              contactPersonPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.contactPersonPhoneNumber &&
              errors.contactPersonPhoneNumber
            )
          }
          errorText={errors.contactPersonPhoneNumber}
          value={formData.contactPersonPhoneNumber}
        />

        <Title size={5}>Skaden</Title>

        <FormInput
          id="propertyInspectorPhoneNumber"
          labelText="Telefonnummer til ejendomsinspektør *"
          name="propertyInspectorPhoneNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("propertyInspectorPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              propertyInspectorPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.propertyInspectorPhoneNumber &&
              errors.propertyInspectorPhoneNumber
            )
          }
          errorText={errors.propertyInspectorPhoneNumber}
          value={formData.propertyInspectorPhoneNumber}
        />
        <FormTextArea
          id="damagedPropertyAddress"
          labelText="Adresse på den skadesramte ejendom *"
          maxLength={500}
          placeholder="Oplys vejnavn og nummer, by samt postnummer."
          value={formData.damagedPropertyAddress}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damagedPropertyAddress", event.target.value);
            setFormData({
              ...formData,
              damagedPropertyAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.damagedPropertyAddress && errors.damagedPropertyAddress)
          }
          errorText={errors.damagedPropertyAddress}
        />
        <FormInputGroup
          labelText="Er ejendommen momsregistreret? *"
          id="isPropertyVATRegistered"
          onChange={(event) => {
            setFormData({
              ...formData,
              isPropertyVATRegistered: event.at(0) ?? "",
            });
            setFieldValue("isPropertyVATRegistered", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.isPropertyVATRegistered}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.isPropertyVATRegistered && errors.isPropertyVATRegistered
            )
          }
          errorText={errors.isPropertyVATRegistered}
        />
        <FormInputGroup
          labelText="Ejendommens type *"
          id="propertyType"
          onChange={(event) => {
            setFormData({
              ...formData,
              propertyType: event.at(0) ?? "",
            });
            setFieldValue("propertyType", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.propertyType}
          onBlur={handleBlur}
          invalid={!!(touched.propertyType && errors.propertyType)}
          errorText={errors.propertyType}
        />
        <FormInputGroup
          labelText="Den skadesramte bygning anvendes til *"
          id="propertyUse"
          onChange={(event) => {
            setFormData({
              ...formData,
              propertyUse: event.at(0) ?? "",
            });
            setFieldValue("propertyUse", event.at(0) ?? "");
          }}
          options={[
            {
              label: "beboelse",
              value: "beboelse",
            },
            {
              label: "erhverv",
              value: "erhverv",
            },
            {
              label: "både beboelse og erhverv",
              value: "både beboelse og erhverv",
            },
          ]}
          type="radio"
          value={formData.propertyUse}
          onBlur={handleBlur}
          invalid={!!(touched.propertyUse && errors.propertyUse)}
          errorText={errors.propertyUse}
        />
        <FormInputGroup
          labelText="Er der også tegnet forsikring i et andet selskab? *"
          id="insuredByOther"
          onChange={(event) => {
            setFormData({
              ...formData,
              insuredByOther: event.at(0) ?? "",
            });
            setFieldValue("insuredByOther", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.insuredByOther}
          onBlur={handleBlur}
          invalid={!!(touched.insuredByOther && errors.insuredByOther)}
          errorText={errors.insuredByOther}
        />
        {formData.insuredByOther === "Ja" && (
          <FormTextArea
            id="insuranceCompanyDetails"
            labelText="Hvilket selskab?"
            maxLength={500}
            placeholder="Oplys forsikringsselskabets navn, policenummer og forsikringssum."
            value={formData.insuranceCompanyDetails}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              setFieldValue("insuranceCompanyDetails", event.target.value);
              setFormData({
                ...formData,
                insuranceCompanyDetails: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={
              !!(
                touched.insuranceCompanyDetails &&
                errors.insuranceCompanyDetails
              )
            }
            errorText={errors.insuranceCompanyDetails}
          />
        )}
        <FormTextArea
          id="damageDetails"
          labelText="Hvordan skete skaden og hvad har forårsaget den? *"
          maxLength={500}
          placeholder="Oplys forsikringsselskabets navn, policenummer og forsikringssum."
          value={formData.damageDetails}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damageDetails", event.target.value);
            setFormData({
              ...formData,
              damageDetails: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageDetails && errors.damageDetails)}
          errorText={errors.damageDetails}
        />
        <FormTextArea
          id="insuranceItemsDamageDetails"
          labelText="Hvilke forsikringsgenstande (bygningsdele og/eller installationer) er beskadiget - og i hvilket omfang? *"
          maxLength={500}
          value={formData.insuranceItemsDamageDetails}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("insuranceItemsDamageDetails", event.target.value);
            setFormData({
              ...formData,
              insuranceItemsDamageDetails: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.insuranceItemsDamageDetails &&
              errors.insuranceItemsDamageDetails
            )
          }
          errorText={errors.insuranceItemsDamageDetails}
        />
        <FormInput
          id="damageDate"
          labelText="Hvornår skete skaden? *"
          name="damageDate"
          placeholder="Dato og klokkeslæt/tidsrum"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("damageDate", event.target.value);
            setFormData({
              ...formData,
              damageDate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageDate && errors.damageDate)}
          errorText={errors.damageDate}
          value={formData.damageDate}
        />
        <FormInput
          id="damageDiscovered"
          labelText="Hvornår blev skaden opdaget? *"
          name="damageDiscovered"
          placeholder="Dato og klokkeslæt"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("damageDiscovered", event.target.value);
            setFormData({
              ...formData,
              damageDiscovered: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageDiscovered && errors.damageDiscovered)}
          errorText={errors.damageDiscovered}
          value={formData.damageDiscovered}
        />
        <FormInputGroup
          labelText="Er andre skyld i skaden? *"
          id="othersToBlame"
          onChange={(event) => {
            setFormData({
              ...formData,
              othersToBlame: event.at(0) ?? "",
            });
            setFieldValue("othersToBlame", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.othersToBlame}
          onBlur={handleBlur}
          invalid={!!(touched.othersToBlame && errors.othersToBlame)}
          errorText={errors.othersToBlame}
        />
        {formData.othersToBlame === "Ja" && (
          <FormInput
            id="whoToBlame"
            labelText="Hvem? *"
            name="whoToBlame"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("whoToBlame", event.target.value);
              setFormData({
                ...formData,
                whoToBlame: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={!!(touched.whoToBlame && errors.whoToBlame)}
            errorText={errors.whoToBlame}
            value={formData.whoToBlame}
          />
        )}
        <FormDropdown
          id="typeOfDamage"
          labelText="Hvilken skade er sket? *"
          options={typeOfDamageDropdown.map((option) => {
            return { labelText: option, value: option };
          })}
          value={formData.typeOfDamage}
          onChange={(values, value) => {
            typeOfDamageDropdownChange(value);
            setFieldValue("typeOfDamage", value);
          }}
          onBlur={handleBlur}
          invalid={!!(touched.typeOfDamage && errors.typeOfDamage)}
          errorText={errors.typeOfDamage}
        />
        <FormTextArea
          id="glassDamageDetails"
          labelText="Udfyld kun hvis glasskade: Beskriv den beskadigede rude"
          maxLength={500}
          placeholder="Er det termoglas eller en anden slags glas? Hvor gammel er ruden? Er ruden monteret i et erhvervslokale? Størrelse på ruden (højde x bredde i cm)"
          value={formData.glassDamageDetails}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("glassDamageDetails", event.target.value);
            setFormData({
              ...formData,
              glassDamageDetails: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.glassDamageDetails && errors.glassDamageDetails)}
          errorText={errors.glassDamageDetails}
        />

        <FormTextArea
          id="theftDamageDetails"
          labelText="Udfyld kun hvis tyveri: Beskriv skaden"
          maxLength={500}
          placeholder="Er der optaget politirapport? På hvilken politistation? Er der tegn på indbrud og vold?"
          value={formData.theftDamageDetails}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("theftDamageDetails", event.target.value);
            setFormData({
              ...formData,
              theftDamageDetails: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.theftDamageDetails && errors.theftDamageDetails)}
          errorText={errors.theftDamageDetails}
        />

        <FormTextArea
          id="sanitationDamdageDetails"
          labelText="Udfyld kun hvis skade på sanitet: Beskriv skaden"
          maxLength={500}
          placeholder="Type (fx håndvask, toilet, bidet, højtskyllende, lavtskyllende), farve og fabrikat"
          value={formData.sanitationDamdageDetails}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("sanitationDamdageDetails", event.target.value);
            setFormData({
              ...formData,
              sanitationDamdageDetails: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.sanitationDamdageDetails &&
              errors.sanitationDamdageDetails
            )
          }
          errorText={errors.sanitationDamdageDetails}
        />
        <FormTextArea
          id="carpetDamageDetails"
          labelText="Udfyld kun hvis skade på gulvtæppe: Beskriv skaden"
          maxLength={500}
          placeholder="Tæppets underlag (fx træ, beton), fabrikat, størrelse (bredde x længde i meter), tæppets alder, købspris, skadens omfang (størrelse i cm). Er tæppet fastgjort? Er der bestilt rensning?"
          value={formData.carpetDamageDetails}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("carpetDamageDetails", event.target.value);
            setFormData({
              ...formData,
              carpetDamageDetails: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.carpetDamageDetails && errors.carpetDamageDetails)
          }
          errorText={errors.carpetDamageDetails}
        />
        <FormInputGroup
          labelText="Hvem kan udbedre skaden? *"
          id="repairDamagePerson"
          onChange={(event) => {
            setFieldValue("repairDamagePerson", event);
            setFormData({
              ...formData,
              repairDamagePerson: event,
            });
          }}
          options={[
            {
              label: "Murer",
              value: "Murer",
            },
            {
              label: "Maler",
              value: "Maler",
            },
            {
              label: "Blikkenslager",
              value: "Blikkenslager",
            },
            {
              label: "Tømrer",
              value: "Tømrer",
            },
            {
              label: "El-installatør",
              value: "El-installatør",
            },
            {
              label: "Glarmester",
              value: "Glarmester",
            },
          ]}
          type="checkbox"
          value={formData.repairDamagePerson}
          onBlur={handleBlur}
        />
        <FormInput
          id="priceRepairEstimate"
          labelText="Overslag for udbedring i kroner *"
          helpText="Håndværkerens og dit eget overslag. Husk at angive om prisen er med eller uden moms. Vi beder dig desuden eftersende overslaget samt opgørelse over skaden på info@bygfs.dk"
          name="priceRepairEstimate"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("priceRepairEstimate", event.target.value);
            setFormData({
              ...formData,
              priceRepairEstimate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.priceRepairEstimate && errors.priceRepairEstimate)
          }
          errorText={errors.priceRepairEstimate}
          value={formData.priceRepairEstimate}
        />

        <Title size={5}>Udbedring af skaden</Title>
        <Text>
          Vi gør opmærksom på, at udbedring af skaden ikke må finde sted, før
          selskabet har givet samtykke hertil, og at udskiftede dele ikke må
          bortkastes uden aftale med selskabet.
        </Text>

        <FormInput
          id="consent-id"
          labelText="Jeg erklærer herved, at foranstående oplysninger er i overensstemmelse med de faktiske forhold, og at jeg vil yde selskabet enhver assistance i forbindelse med skaden *"
          name="consent"
          type="checkbox"
          value="consent"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("consent", event.target.checked);
            setFormData({
              ...formData,
              consent: event.target.checked,
            });
          }}
          onBlur={handleBlur}
          checked={formData.consent}
        />
        {touched.consent && errors.consent && (
          <>
            <br />
            <CustomError errorText={errors.consent} />
          </>
        )}
        <Button loading={isLoading} variant={"primary"} type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default ClaimReportBuilding;
